import { Data, List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { EnumHelper, IEnumItem } from '@singularsystems/neo-core/dist/Model/EnumHelper';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { SQLTextValidationEnum } from '../../../Common/Models/SQLTextValidationEnum';
import { TextConstants } from '../../../Common/TextConstants';
import ManageRawGenericMappingComponentVM from '../Components/ManageRawGenericMappingComponentVM';
import CreateRawGenericMappingCommand from '../Models/Commands/CreateRawGenericMappingCommand';
import UpdateRawGenericMappingCommand from '../Models/Commands/UpdateRawGenericMappingCommand';
import RawGenericMappingLookup from '../Models/Lookups/RawGenericMappingLookup';
import RawGenericMappingSearchCriteria from '../Models/Queries/RawGenericMappingSearchCriteria';
import DestinationTableLookup from '../Models/Lookups/DestinationTableLookup';

@NeoModel
export default class RawGenericMappingVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public rawGenericMappingApiClient = AppService.get(Types.ApiClients.RawGenericMappingApiClient),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
    }

    public rawGenericMappingSearchCriteria = new RawGenericMappingSearchCriteria();
    public manageRawGenericMappingVM = new ManageRawGenericMappingComponentVM();
    public showManageMappingsComponent: boolean = false;
    public sqlTextValidationEnumList: IEnumItem[] = []
    public errorCount: number = 0;
    public commandIsValid: boolean = true;
    public destinationTableList = new List(DestinationTableLookup);

    public pageManager = new Data.PageManager(this.rawGenericMappingSearchCriteria, RawGenericMappingLookup,
        this.rawGenericMappingApiClient.getRawGenericMappingsPagedList, {
        pageSize: 10,
        sortBy: "fileSpecProperties",
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    })

    public async initialise() {
        await this.loadDestinationTables();
        this.populateSQLTextValidationEnumList();
        reaction(() => this.rawGenericMappingSearchCriteria.sourceTable, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.rawGenericMappingSearchCriteria.iteration, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.rawGenericMappingSearchCriteria.sourceField, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.rawGenericMappingSearchCriteria.defaultValue, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.rawGenericMappingSearchCriteria.destinationFieldId, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 0 })
        reaction(() => this.rawGenericMappingSearchCriteria.destinationTableId, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 0 })
        reaction(() => this.rawGenericMappingSearchCriteria.isGenericJoinField, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 0 })
    }

    public async addNewGenericMapping() {
        this.showManageMappingsComponent = true;
        await this.manageRawGenericMappingVM.initialise();
        this.manageRawGenericMappingVM.managedGenericMapping = new RawGenericMappingLookup();
    }

    public async cancel() {
        this.manageRawGenericMappingVM.managedGenericMapping = new RawGenericMappingLookup();
        this.resetIndicators();
        await this.pageManager.refreshData();
    }

    public async clearSearch() {
        this.rawGenericMappingSearchCriteria = new RawGenericMappingSearchCriteria();
        await this.pageManager.refreshData();
    }

    public async editGenericMapping(genericMapping: RawGenericMappingLookup) {
        this.manageRawGenericMappingVM.managedGenericMapping.mapFrom(genericMapping).toJSObject();
        this.showManageMappingsComponent = true;
        this.manageRawGenericMappingVM.isFieldReadOnlyInd = true;
        await this.manageRawGenericMappingVM.initialise();
    }

    public async loadDestinationTables() {
        const response = await this.taskRunner.waitFor(this.rawGenericMappingApiClient.getDestinationTablesList());
        this.destinationTableList.set(response.data);
    }

    public resetIndicators() {
        this.showManageMappingsComponent = false;
        this.manageRawGenericMappingVM.isFieldReadOnlyInd = false;
        this.manageRawGenericMappingVM.setSourceFieldValuesToFalse();
    }

    public async saveGenericMapping() {
        this.validateCommand(this.manageRawGenericMappingVM.managedGenericMapping);
        if (this.commandIsValid) {
            this.taskRunner.run(async () => {
                var response = this.manageRawGenericMappingVM.managedGenericMapping.rawGenericMappingID > 0
                    ? await this.rawGenericMappingApiClient.updateRawGenericMapping((this.mapGenericMappingToUpdateCommand()!.toJSObject()))
                    : await this.rawGenericMappingApiClient.addNewRawGenericMapping((this.mapGenericMappingToCreateCommand()!.toJSObject()));

                if (response.data.success) {
                    await this.pageManager.refreshData();
                    NotifyUtils.addSuccess(TextConstants.Titles.Success, "Generic mapping has been successfully saved.", NotificationDuration.Standard);
                }
                else {
                    NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                }
                this.resetIndicators();
            })
        }
    }

    private mapGenericMappingToCreateCommand() {
        var command = new CreateRawGenericMappingCommand().mapFrom(this.manageRawGenericMappingVM.managedGenericMapping);
        return command;
    }

    private mapGenericMappingToUpdateCommand() {
        var command = new UpdateRawGenericMappingCommand().mapFrom(this.manageRawGenericMappingVM.managedGenericMapping);
        return command;
    }


    private validateCommand(command: RawGenericMappingLookup) {
        if (command.calculation !== null && command.calculation !== "") {
            this.validateSQL(command.calculation.toUpperCase());
        }

        if (this.errorCount > 0) {
            NotifyUtils.add(TextConstants.Titles.Error, TextConstants.Validation.InvalidSQLGenericMapping, "danger" as any, NotificationDuration.Long);
        }
    }

    private populateSQLTextValidationEnumList() {
        this.sqlTextValidationEnumList = EnumHelper.asList(SQLTextValidationEnum);
    }

    private validateSQL(sql: string) {
        for (var i = 0; i < this.sqlTextValidationEnumList.length; i++) {
            if (sql.includes(this.sqlTextValidationEnumList[i].name)) {
                this.commandIsValid = false;
                this.errorCount++;
            }
        }
    }
}